import React, { useState } from 'react';
import { convertPDFToText } from '@/services/pdfConversionService';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';

export default function ClarissaResume() {
  const [text, setText] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { toast } = useToast();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setText(''); // Clear previous text
    } else {
      toast({
        title: "Invalid file",
        description: "Please select a PDF file",
        variant: "destructive",
      });
    }
  };

  const handleConvertPDF = async () => {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Please select a PDF file first",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Upload file to Supabase storage
      const fileName = `${Date.now()}-${selectedFile.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('resumes')
        .upload(fileName, selectedFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('resumes')
        .getPublicUrl(fileName);

      // Convert PDF to text
      const result = await convertPDFToText(publicUrl);
      
      if (result.success && result.text) {
        setText(result.text);
        toast({
          title: "Success",
          description: "PDF converted to text successfully",
        });
      } else {
        throw new Error(result.error || "Failed to convert PDF");
      }
    } catch (error) {
      toast({
        title: "Error", 
        description: error instanceof Error ? error.message : "Failed to convert PDF to text",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <div className="space-y-4">
        <div>
          <label htmlFor="pdf-upload" className="block text-sm font-medium mb-2">
            Select PDF file to convert:
          </label>
          <Input
            id="pdf-upload"
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            className="mb-4"
          />
        </div>
        
        <Button 
          onClick={handleConvertPDF} 
          disabled={loading || !selectedFile}
          className="w-full sm:w-auto"
        >
          {loading ? 'Converting...' : 'Convert PDF to Text'}
        </Button>
        
        {text && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-3">Extracted Text:</h3>
            <pre className="whitespace-pre-wrap font-mono text-sm border rounded p-4 bg-muted max-h-96 overflow-y-auto">
              {text}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}